import React, { useState, useRef, useEffect, useCallback } from 'react';
import { appConfig } from '../../config/appConfig';
import { Search, X, Clock, TrendingUp } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';

interface SearchSuggestion {
  term: string;
  type: 'recent' | 'popular' | 'suggestion';
  frequency?: number;
}

interface SearchWithAutoCompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSearch: (term: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  recentSearches?: string[];
  onRecentSearchClick?: (search: string) => void;
  onClearRecent?: () => void;
}

export const SearchWithAutoComplete: React.FC<SearchWithAutoCompleteProps> = ({
  value,
  onChange,
  onSearch,
  placeholder = "Search prompts, tags, or content...",
  className,
  disabled,
  recentSearches = [],
  onRecentSearchClick,
  onClearRecent
}) => {
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [popularTerms, setPopularTerms] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Fetch popular terms on mount
  useEffect(() => {
    const fetchPopularTerms = async () => {
      try {
        const response = await fetch(`${appConfig.apiUrl}/prompts/popular-searches?limit=5`);
        if (response.ok) {
          const terms = await response.json();
          setPopularTerms(terms);
        }
      } catch (error) {
        console.error('Failed to fetch popular terms:', error);
      }
    };

    fetchPopularTerms();
  }, []);

  // Debounced suggestion fetching
  const fetchSuggestions = async (term: string) => {
    if (term.length < 2) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${appConfig.apiUrl}/prompts/search-suggestions?partial=${encodeURIComponent(term)}`);
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.map((suggestion: string) => ({
          term: suggestion,
          type: 'suggestion' as const
        })));
      }
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedFetch = useCallback((term: string) => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => fetchSuggestions(term), 300);
  }, []);

  useEffect(() => {
    if (value && showSuggestions) {
      debouncedFetch(value);
    } else {
      setSuggestions([]);
    }
  }, [value, showSuggestions, debouncedFetch]);

  // Combine suggestions with recent searches and popular terms
  const allSuggestions = React.useMemo(() => {
    const combined: SearchSuggestion[] = [];
    
    // Add recent searches if no current value
    if (!value && recentSearches.length > 0) {
      combined.push(...recentSearches.slice(0, 3).map(term => ({
        term,
        type: 'recent' as const
      })));
    }

    // Add suggestions
    combined.push(...suggestions.slice(0, 5));

    // Add popular terms if no suggestions and no current value
    if (combined.length === 0 && popularTerms.length > 0) {
      combined.push(...popularTerms.map(term => ({
        term,
        type: 'popular' as const
      })));
    }

    return combined;
  }, [value, suggestions, recentSearches, popularTerms]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    setSelectedIndex(-1);
  };

  const handleInputFocus = () => {
    setShowSuggestions(true);
  };

  const handleInputBlur = (e: React.FocusEvent) => {
    // Delay hiding suggestions to allow clicks on suggestions
    setTimeout(() => {
      if (!suggestionsRef.current?.contains(e.relatedTarget as Node)) {
        setShowSuggestions(false);
      }
    }, 150);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || allSuggestions.length === 0) {
      if (e.key === 'Enter' && value.trim()) {
        e.preventDefault();
        handleSearch(value.trim());
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < allSuggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : allSuggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < allSuggestions.length) {
          const selectedSuggestion = allSuggestions[selectedIndex];
          handleSuggestionClick(selectedSuggestion);
        } else if (value.trim()) {
          handleSearch(value.trim());
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  const handleSuggestionClick = (suggestion: SearchSuggestion) => {
    onChange(suggestion.term);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    
    if (suggestion.type === 'recent' && onRecentSearchClick) {
      onRecentSearchClick(suggestion.term);
    } else {
      handleSearch(suggestion.term);
    }
  };

  const handleSearch = (term: string) => {
    if (term.trim()) {
      onSearch(term.trim());
      setShowSuggestions(false);
    }
  };

  const handleClear = () => {
    onChange('');
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  const getSuggestionIcon = (type: SearchSuggestion['type']) => {
    switch (type) {
      case 'recent':
        return <Clock className="w-4 h-4 text-gray-400" />;
      case 'popular':
        return <TrendingUp className="w-4 h-4 text-blue-500" />;
      default:
        return <Search className="w-4 h-4 text-gray-400" />;
    }
  };

  const getSuggestionLabel = (type: SearchSuggestion['type']) => {
    switch (type) {
      case 'recent':
        return 'Recent';
      case 'popular':
        return 'Popular';
      default:
        return null;
    }
  };

  return (
    <div className={cn("relative w-full", className)}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className="pl-10 pr-8"
          aria-expanded={showSuggestions}
          aria-haspopup="listbox"
          role="combobox"
          aria-autocomplete="list"
        />
        {value && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleClear}
            className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0 hover:bg-gray-100"
            aria-label="Clear search"
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && (
        <div
          ref={suggestionsRef}
          className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-50 max-h-96 overflow-y-auto"
          role="listbox"
        >
          {isLoading && (
            <div className="p-3 text-center text-gray-500 text-sm">
              Loading suggestions...
            </div>
          )}

          {!isLoading && allSuggestions.length === 0 && value && (
            <div className="p-3 text-center text-gray-500 text-sm">
              No suggestions found
            </div>
          )}

          {!isLoading && allSuggestions.length === 0 && !value && (
            <div className="p-3">
              <div className="text-sm font-medium text-gray-700 mb-2">
                Start typing to search prompts...
              </div>
              {popularTerms.length > 0 && (
                <div>
                  <div className="text-xs text-gray-500 mb-2">Popular searches:</div>
                  <div className="flex flex-wrap gap-1">
                    {popularTerms.slice(0, 5).map((term, index) => (
                      <Badge
                        key={index}
                        variant="secondary"
                        className="cursor-pointer hover:bg-blue-100"
                        onClick={() => handleSuggestionClick({ term, type: 'popular' })}
                      >
                        {term}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {!isLoading && allSuggestions.map((suggestion, index) => (
            <div
              key={`${suggestion.type}-${suggestion.term}`}
              className={cn(
                "flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors",
                index === selectedIndex
                  ? "bg-blue-50 text-blue-900"
                  : "hover:bg-gray-50"
              )}
              onClick={() => handleSuggestionClick(suggestion)}
              role="option"
              aria-selected={index === selectedIndex}
            >
              {getSuggestionIcon(suggestion.type)}
              <span className="flex-1 text-sm">{suggestion.term}</span>
              {getSuggestionLabel(suggestion.type) && (
                <Badge variant="outline" className="text-xs">
                  {getSuggestionLabel(suggestion.type)}
                </Badge>
              )}
            </div>
          ))}

          {/* Clear recent searches option */}
          {!value && recentSearches.length > 0 && onClearRecent && (
            <div className="border-t border-gray-100">
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearRecent}
                className="w-full justify-start px-3 py-2 text-sm text-gray-500 hover:text-gray-700"
              >
                <X className="w-4 h-4 mr-2" />
                Clear recent searches
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};